import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAWSProtection } from '../hooks/useAWSProtection';
import { getHomeRoute } from '../utils/homeRoute';
import { AlertCircle, Loader2, Eye, EyeOff, Mail, Lock, ChevronRight } from 'lucide-react';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const { getContextData } = useAWSProtection();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showSessionError, setShowSessionError] = useState(false);
  const [logoError, setLogoError] = useState(false);

  const handleClearSession = () => {
    localStorage.removeItem('niwt_authenticated');
    localStorage.removeItem('niwt_access_token');
    localStorage.removeItem('niwt_id_token');
    localStorage.removeItem('niwt_refresh_token');
    localStorage.removeItem('niwt_user_email');
    localStorage.removeItem('niwt_user_attributes');
    sessionStorage.clear();
    setShowSessionError(false);
    setError('Session cleared. Please try signing in again.');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setShowSessionError(false);
    setLoading(true);

    try {
      const contextData = getContextData(email);
      await signIn(email, password, contextData);
      navigate(getHomeRoute());
    } catch (err: any) {
      const code = err.code || err.name || '';
      const message = err.message || '';

      if (message.toLowerCase().includes('already a signed in user')) {
        setShowSessionError(true);
        setError('Another session is already active. Clear it below to continue.');
      } else if (
        code === 'NotAuthorizedException' ||
        code === 'UserNotFoundException' ||
        code === 'UserNotConfirmedException'
      ) {
        setError('Incorrect email or password. Please try again.');
      } else if (code === 'LimitExceededException' || code === 'TooManyRequestsException') {
        setError('Too many attempts. Please wait a few minutes and try again.');
      } else {
        setError('Sign in failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#2D1B69] to-[#1A0F3D] flex flex-col justify-center py-12 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Grid overlay */}
      <div
        className="absolute inset-0 pointer-events-none opacity-10"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}
      />

      {/* Logo and Title */}
      <div className="sm:mx-auto sm:w-full sm:max-w-md z-10">
        <div className="flex justify-center">
          {logoError ? (
            <div className="inline-flex items-center justify-center w-[60px] h-[60px] bg-niwt-primary rounded-2xl">
              <span className="text-white text-2xl font-bold font-libre">N</span>
            </div>
          ) : (
            <img
              src="/images/niwt-logo.png"
              alt="Niwt"
              width={60}
              height={60}
              onError={() => setLogoError(true)}
            />
          )}
        </div>
        <h1 className="mt-6 text-center text-3xl font-libre font-bold text-white">Welcome to Niwt</h1>
        <p className="mt-2 text-center text-sm text-white/70">Sign in to access your digital twins</p>
      </div>

      {/* Login Form */}
      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md z-10">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div role="alert" className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-start">
                <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" aria-hidden="true" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="w-5 h-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setError(''); }}
                  required
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-niwt-primary focus:border-transparent hover:border-gray-400 transition-all"
                  placeholder="you@company.com"
                  autoComplete="email"
                  disabled={loading}
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="w-5 h-5 text-gray-400" />
                </div>
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setError(''); }}
                  required
                  className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-niwt-primary focus:border-transparent hover:border-gray-400 transition-all"
                  placeholder="••••••••"
                  autoComplete="current-password"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 focus:outline-none focus:text-gray-600"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="flex justify-end">
              <Link
                to="/forgot-password"
                className="text-sm text-niwt-primary hover:text-niwt-secondary font-medium transition-colors"
              >
                Forgot password?
              </Link>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-niwt-primary text-white py-3 px-4 rounded-lg font-semibold hover:bg-[#6B51A0] focus:outline-none focus:ring-2 focus:ring-niwt-primary focus:ring-offset-2 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Signing in...
                </>
              ) : (
                <>
                  Sign In
                  <ChevronRight className="w-5 h-5 ml-2" />
                </>
              )}
            </button>

            {showSessionError && (
              <div>
                <button
                  type="button"
                  onClick={handleClearSession}
                  className="w-full py-2 px-4 border-2 border-niwt-primary rounded-lg text-niwt-primary font-medium hover:bg-niwt-primary/5 focus:outline-none focus:ring-2 focus:ring-niwt-primary focus:ring-offset-2 transition-colors duration-200"
                >
                  Clear Previous Session
                </button>
                <p className="text-xs text-gray-500 text-center mt-2">
                  This will sign out the previous user and allow you to log in
                </p>
              </div>
            )}
          </form>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-8 text-center z-10">
        <div className="flex items-center justify-center gap-4 text-sm">
          <a href="https://www.niwt.com" className="text-white/80 hover:text-white hover:underline transition-colors">niwt.com</a>
          <span className="text-white/40">&bull;</span>
          <a href="https://www.niwt.com/privacy" className="text-white/80 hover:text-white hover:underline transition-colors">Privacy</a>
          <span className="text-white/40">&bull;</span>
          <a href="https://www.niwt.com/terms" className="text-white/80 hover:text-white hover:underline transition-colors">Terms</a>
        </div>
        <div className="mt-4 flex items-center justify-center gap-3">
          <span className="px-3 py-1 bg-white/10 border border-white/20 rounded text-xs font-semibold text-white/60 tracking-wider">SOC2</span>
          <span className="px-3 py-1 bg-white/10 border border-white/20 rounded text-xs font-semibold text-white/60 tracking-wider">ENCRYPTED</span>
        </div>
      </div>
    </div>
  );
};

export default Login;
